/**
 * Block-aware HTML sanitizer for config-driven rich text (e.g. order success
 * body). Allows paragraphs, lists, headings, line breaks, and inline emphasis
 * — sufficient to author the post-submit modal copy without enabling arbitrary
 * scripts, styles, or media.
 *
 * Inline-only equivalent lives in :func:`sanitizeLimitedInlineHtml`. Use that
 * for short cell-level disclosures and this one for multi-line modal/page
 * content.
 */

const ALLOWED_TAGS = new Set([
  'B',
  'STRONG',
  'EM',
  'I',
  'U',
  'BR',
  'P',
  'SPAN',
  'DIV',
  'UL',
  'OL',
  'LI',
  'H1',
  'H2',
  'H3',
  'H4',
  'H5',
  'H6',
  'A',
])

const SAFE_URL_RE = /^(https?:|mailto:|tel:|\/|#)/i

function cleanElement(el: Element): void {
  let i = 0
  while (i < el.childNodes.length) {
    const node = el.childNodes[i]
    if (node.nodeType === Node.TEXT_NODE) {
      i += 1
      continue
    }
    if (node.nodeType !== Node.ELEMENT_NODE) {
      el.removeChild(node)
      continue
    }
    const child = node as Element
    if (!ALLOWED_TAGS.has(child.tagName)) {
      while (child.firstChild) {
        el.insertBefore(child.firstChild, child)
      }
      el.removeChild(child)
      continue
    }
    const keepHref = child.tagName === 'A' ? child.getAttribute('href') : null
    while (child.attributes.length > 0) {
      child.removeAttribute(child.attributes[0].name)
    }
    if (keepHref && SAFE_URL_RE.test(keepHref.trim())) {
      child.setAttribute('href', keepHref.trim())
      child.setAttribute('rel', 'noopener noreferrer')
      child.setAttribute('target', '_blank')
    }
    cleanElement(child)
    i += 1
  }
}

/**
 * Strips disallowed tags (unwraps children), removes all attributes, and
 * normalizes anchor hrefs to safe URL schemes. Mitigates XSS for trusted-but-
 * structured config HTML.
 */
export function sanitizeRichHtml(html: string): string {
  if (!html || typeof document === 'undefined') {
    return html
  }
  const doc = new DOMParser().parseFromString(html, 'text/html')
  cleanElement(doc.body)
  return doc.body.innerHTML
}
